/**
 * ImportEmailCommentsDialog Component
 *
 * Dialog for importing review comments from pasted email text.
 * Parses common email formats and extracts structured comments.
 *
 * Features:
 * - Paste email text to parse
 * - Preview extracted comments before import
 * - Edit parsed comments before saving
 * - Confidence indicators for auto-detected fields
 *
 * Part of Phase 3: Email parsing for automated comment import (Future)
 */

import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import {
  Mail,
  Loader2,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  FileText,
  ChevronRight,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import {
  parseEmailForComments,
  type EmailParseResult,
  type ParsedReviewComment,
} from '@/lib/email-parser';
import type { ReviewComment } from '@/types/jurisdiction';

interface ImportEmailCommentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (comments: Array<Omit<ReviewComment, 'id' | 'jobId' | 'createdAt' | 'updatedAt'>>) => Promise<void>;
  currentReviewCycle?: number;
}

type Step = 'paste' | 'preview' | 'importing';

export function ImportEmailCommentsDialog({
  open,
  onOpenChange,
  onImport,
  currentReviewCycle = 1,
}: ImportEmailCommentsDialogProps) {
  const [step, setStep] = useState<Step>('paste');
  const [emailText, setEmailText] = useState('');
  const [parseResult, setParseResult] = useState<EmailParseResult | null>(null);
  const [selectedComments, setSelectedComments] = useState<Set<number>>(new Set());
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when dialog opens/closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setStep('paste');
      setEmailText('');
      setParseResult(null);
      setSelectedComments(new Set());
      setError(null);
    }
    onOpenChange(newOpen);
  };

  const handleParse = () => {
    if (!emailText.trim()) {
      setError('Please paste the email text');
      return;
    }

    setError(null);
    const result = parseEmailForComments(emailText);
    setParseResult(result);

    if (result.success && result.comments.length > 0) {
      // Pre-select all comments
      setSelectedComments(new Set(result.comments.map((_, i) => i)));
      setStep('preview');
    } else {
      setError(result.errors.join('. ') || 'No comments could be extracted from the email');
    }
  };

  const toggleComment = (index: number) => {
    const newSelected = new Set(selectedComments);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedComments(newSelected);
  };

  const toggleAll = () => {
    if (parseResult) {
      if (selectedComments.size === parseResult.comments.length) {
        setSelectedComments(new Set());
      } else {
        setSelectedComments(new Set(parseResult.comments.map((_, i) => i)));
      }
    }
  };

  const handleImport = async () => {
    if (!parseResult || selectedComments.size === 0) return;

    setIsImporting(true);
    setStep('importing');
    setError(null);

    try {
      const commentsToImport = parseResult.comments
        .filter((_, index) => selectedComments.has(index))
        .map((parsed) => ({
          originalText: parsed.originalText,
          source: parsed.source,
          sourceName: parsed.sourceName,
          reviewerName: parsed.reviewerName,
          category: parsed.category,
          reviewCycle: parsed.reviewCycle || currentReviewCycle,
          pageReference: parsed.pageReference,
          sheetReference: parsed.sheetReference,
          status: 'open' as const,
          receivedAt: new Date().toISOString(),
        }));

      await onImport(commentsToImport);
      handleOpenChange(false);
    } catch (err) {
      console.error('Failed to import comments:', err);
      setError('Failed to import comments. Please try again.');
      setStep('preview');
    } finally {
      setIsImporting(false);
    }
  };

  const confidenceColor = (confidence: 'high' | 'medium' | 'low') => {
    switch (confidence) {
      case 'high':
        return 'text-green-600 bg-green-100 dark:bg-green-900/30';
      case 'medium':
        return 'text-amber-600 bg-amber-100 dark:bg-amber-900/30';
      case 'low':
        return 'text-red-600 bg-red-100 dark:bg-red-900/30';
    }
  };

  const categoryLabels: Record<string, string> = {
    plans_correction: 'Plans Correction',
    missing_document: 'Missing Document',
    code_violation: 'Code Violation',
    clarification_needed: 'Clarification',
    fee_issue: 'Fee Issue',
    site_issue: 'Site Issue',
    approval_condition: 'Approval Condition',
    other: 'Other',
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Import Comments from Email
          </DialogTitle>
          <DialogDescription>
            {step === 'paste' && 'Paste the email text containing review comments. We\'ll automatically extract and categorize them.'}
            {step === 'preview' && 'Review the extracted comments and select which ones to import.'}
            {step === 'importing' && 'Importing your comments...'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden py-4">
          {/* Step 1: Paste Email */}
          {step === 'paste' && (
            <div className="space-y-4 h-full">
              <div className="space-y-2">
                <Label htmlFor="email-text">Email Text</Label>
                <Textarea
                  id="email-text"
                  value={emailText}
                  onChange={(e) => setEmailText(e.target.value)}
                  placeholder="Paste the email text here...

Example:
Subject: Plan Review Comments - Permit #BLD-2024-12345

Dear Contractor,

Please address the following comments:

1. Page 3: Show smoke detector locations per NFPA 72
2. Sheet E-101: Provide load calculations for panel
3. Clarify the type of insulation being used..."
                  className="min-h-[300px] font-mono text-sm"
                />
              </div>

              <Alert className="bg-primary/5 border-primary/20">
                <Sparkles className="h-4 w-4 text-primary" />
                <AlertTitle className="text-sm">Smart Parsing</AlertTitle>
                <AlertDescription className="text-xs">
                  We'll automatically detect the department, review cycle, and categorize each comment.
                  You can review and edit before importing.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && parseResult && (
            <div className="space-y-4 h-full flex flex-col">
              {/* Metadata Summary */}
              <div className="flex flex-wrap gap-2 items-center p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-1 text-sm">
                  <Info className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Detected:</span>
                </div>
                {parseResult.metadata.permitNumber && (
                  <Badge variant="secondary">Permit: {parseResult.metadata.permitNumber}</Badge>
                )}
                {parseResult.metadata.sourceDepartment && (
                  <Badge variant="secondary">{parseResult.metadata.sourceDepartment}</Badge>
                )}
                <Badge variant="outline">Review Cycle {parseResult.metadata.reviewCycle}</Badge>
                <Badge variant="outline">{parseResult.comments.length} comments found</Badge>
              </div>

              {/* Select All */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="select-all"
                    checked={selectedComments.size === parseResult.comments.length}
                    onCheckedChange={toggleAll}
                  />
                  <label htmlFor="select-all" className="text-sm cursor-pointer">
                    Select All ({selectedComments.size} of {parseResult.comments.length})
                  </label>
                </div>
              </div>

              {/* Comments List */}
              <ScrollArea className="flex-1 border rounded-lg">
                <div className="p-2 space-y-2">
                  {parseResult.comments.map((comment, index) => (
                    <div
                      key={index}
                      className={cn(
                        'flex gap-3 p-3 rounded-lg border transition-colors',
                        selectedComments.has(index)
                          ? 'bg-primary/5 border-primary/20'
                          : 'hover:bg-muted/50'
                      )}
                    >
                      <Checkbox
                        id={`comment-${index}`}
                        checked={selectedComments.has(index)}
                        onCheckedChange={() => toggleComment(index)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge
                            variant="outline"
                            className={cn('text-xs shrink-0', confidenceColor(comment.confidence))}
                          >
                            {comment.confidence}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {categoryLabels[comment.category] || comment.category}
                          </Badge>
                          {comment.pageReference && (
                            <span className="text-xs text-muted-foreground">
                              {comment.pageReference}
                            </span>
                          )}
                          {comment.sheetReference && (
                            <span className="text-xs text-muted-foreground">
                              Sheet {comment.sheetReference}
                            </span>
                          )}
                        </div>
                        <p className="text-sm line-clamp-3">{comment.originalText}</p>
                      </div>
                      {selectedComments.has(index) && (
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-1" />
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Step 3: Importing */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Importing {selectedComments.size} comments...</p>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          {step === 'paste' && (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleParse} disabled={!emailText.trim()}>
                <Sparkles className="h-4 w-4 mr-2" />
                Parse Email
              </Button>
            </>
          )}

          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('paste')}>
                Back
              </Button>
              <Button onClick={handleImport} disabled={selectedComments.size === 0}>
                <FileText className="h-4 w-4 mr-2" />
                Import {selectedComments.size} Comments
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ImportEmailCommentsDialog;
